<script lang="ts">
  import { 
    createServicePinsForFloor, 
    clearServicePins,
    clearServiceLabels
  } from './serviceLocationUtils';
  import type { ServiceLocationData, ServiceLocationConfig } from './serviceLocationTypes';
  import { debugLog } from '../store-layer/debugUtils';

  // Props
  export let svgElement: SVGSVGElement | null = null;
  export let serviceLocations: ServiceLocationData[] = [];
  export let config: ServiceLocationConfig;

  // Track last rendered state to avoid unnecessary re-renders
  let lastRenderedServiceLocations: ServiceLocationData[] = [];
  let lastRenderedConfig: ServiceLocationConfig | null = null;

  // Check if service locations or config have changed significantly
  function hasSignificantChange(
    newServiceLocations: ServiceLocationData[],
    newConfig: ServiceLocationConfig
  ): boolean {
    // Check if service locations changed
    if (newServiceLocations.length !== lastRenderedServiceLocations.length) {
      return true;
    }

    // Check if service location IDs changed
    const newIds = new Set(newServiceLocations.map(s => s.id));
    const oldIds = new Set(lastRenderedServiceLocations.map(s => s.id));
    if (newIds.size !== oldIds.size || [...newIds].some(id => !oldIds.has(id))) {
      return true;
    }

    // Check if relevant config properties changed
    if (!lastRenderedConfig) return true;
    
    const configPropsToCheck = [
      'enabled',
      'showServicePins',
      'pinStyling',
      'labelStyling.enabled'
    ];

    for (const prop of configPropsToCheck) {
      const newValue = getNestedProperty(newConfig, prop);
      const oldValue = getNestedProperty(lastRenderedConfig, prop);
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        return true;
      }
    }

    return false;
  }

  // Helper function to get nested property
  function getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  // Main rendering function
  async function renderServiceLocations() {
    if (!svgElement || !config.enabled) {
      clearServicePins(svgElement!);
      clearServiceLabels(svgElement!);
      return;
    }

    debugLog(`Rendering ${serviceLocations.length} service locations`, 'serviceLocations');

    // Create service pins (which include hover label functionality)
    if (config.showServicePins) {
      await createServicePinsForFloor(
        svgElement,
        serviceLocations,
        config
      );
    } else {
      clearServicePins(svgElement);
    }

    // Clear any existing permanent labels since we only want hover labels
    clearServiceLabels(svgElement);

    // Update tracking variables
    lastRenderedServiceLocations = [...serviceLocations];
    lastRenderedConfig = JSON.parse(JSON.stringify(config));
  }

  // Reactive statements
  $: if (svgElement && hasSignificantChange(serviceLocations, config)) {
    renderServiceLocations();
  }

  // Export functions for external access
  export async function forceRender() {
    await renderServiceLocations();
  }

  export function clearAll() {
    if (svgElement) {
      clearServicePins(svgElement);
      clearServiceLabels(svgElement);
    }
    lastRenderedServiceLocations = [];
    lastRenderedConfig = null;
  }
</script>

<!-- This component doesn't render any HTML - it only manipulates the SVG DOM --> 